import React, { useState, useEffect } from 'react';
import { Trophy, LogOut, AlertCircle, RefreshCw, Key, X, ChevronRight } from 'lucide-react';
import usePermissionStore from '../../stores/permissionStore';
import { authService, studentService, purchaseService, rewardService } from '../../services';
import { formatHours } from '../../utils/helpers';
import { useToast } from '../../hooks/useToast';
import { setWeChatShare, SHARE_TEMPLATE } from '../../utils/wechat';

const StudentProfileView = ({ onClose }) => {
  const [studentInfo, setStudentInfo] = useState(null);
  const [studentId, setStudentId] = useState(null); // 存储关联的学生ID
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false); // 记录是否正在手动刷新
  
  // 密码修改相关状态
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // 注意：role 在 permissionStore 中是单独存储的，不是 user 对象的属性
  const { user, role, logout } = usePermissionStore();
  const toast = useToast();

  // 加载学生信息
  const loadStudentInfo = async (isRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      if (isRefresh) setIsRefreshing(true);
      
      const commonParams = isRefresh ? { is_refresh: 'true' } : {};
      
      // 确保用户信息完整且有ID
      if (!user) {
        throw new Error('用户信息未加载');
      }
      
      if (!user.id) {
        throw new Error('用户ID缺失');
      }
      
      console.log('尝试加载学生信息，用户ID:', user.id, '用户角色:', role);
      
      // 检查是否为令牌访问模式
      const isTokenAccess = localStorage.getItem('access_token') !== null;
      if (isTokenAccess && role === 'student') {
        console.log('令牌访问模式：使用已保存的学生信息');
        // 从本地存储或状态获取临时的学生名
        const name = localStorage.getItem('access_student_name') || user?.username || '学生';
        const targetStudentId = studentId || usePermissionStore.getState().getStudentId();
        
        setStudentInfo({
          id: targetStudentId,
          name: name,
        });
        setStudentId(targetStudentId);
        setLoading(false);
        setIsRefreshing(false);
        return; 
      }

      // 检查是否为演示模式
      const isDemoMode = import.meta.env.VITE_USE_API_AUTH !== 'true';
      
      if (isDemoMode) {
        // 演示模式下，创建模拟学生数据
        console.log('演示模式：创建模拟学生数据');
        const mockStudent = {
          id: user.id,
          name: user.username || `学生${user.id}`,
          course_name: '美术基础班',
          points: 150,
        };
        setStudentInfo(mockStudent);
        setStudentId(user.id);
      } else {
        // API模式下，获取学生关联信息
        let student = null;
        let linkedStudentId = null;
        
        if (role === 'student') {
          // 学生角色：通过 /me/student-link 端点获取关联的学生信息
          try {
            console.log('学生角色，通过 /me/student-link 获取关联学生信息');
            // authService.getCurrentStudentInfo 内部默认调用请求，这里我们手动带上 param 如果是刷新
            const studentLink = await authService.getCurrentStudentInfo(commonParams);
            
            if (studentLink && studentLink.student_id) {
              linkedStudentId = studentLink.student_id;
              // 如果返回中包含完整学生信息，直接使用
              if (studentLink.student) {
                student = studentLink.student;
              } else {
                // 否则通过学生ID获取详细信息
                student = await studentService.getById(linkedStudentId, commonParams);
              }
              console.log('成功获取关联学生信息:', student);
            } else {
              console.warn('未找到学生关联信息');
              throw new Error('未找到关联的学生信息，请联系管理员绑定学生账号');
            }
          } catch (err) {
            console.error('获取学生关联信息失败:', err);
            throw err;
          }
        } else {
          // 非学生角色（管理员等）直接通过ID获取
          student = await studentService.getById(user.id, commonParams);
          linkedStudentId = user.id;
        }
        
        console.log('成功获取学生信息:', student);
        setStudentInfo(student);
        setStudentId(linkedStudentId);
      }
    } catch (err) {
      console.error('加载学生信息失败:', err);
      // 根据错误类型提供更具体的错误信息
      let errorMessage = err.message || '加载失败';
      
      // 如果是404错误，说明可能用户ID不是学生ID
      if (err.status === 404) {
        errorMessage = `学生信息不存在 (ID: ${user?.id})`;
      } else if (err.message?.includes('404')) {
        errorMessage = `学生信息不存在 (ID: ${user?.id})`;
      } else if (err.status === 403) {
        errorMessage = '权限不足，无法访问学生信息';
      } else if (err.status === 500) {
        errorMessage = '服务器内部错误，请稍后重试';
      } else if (err.message?.includes('Permission')) {
        errorMessage = '权限验证失败，请重新登录';
      }
      
      setError(errorMessage);
      toast.error(`加载学生信息失败: ${errorMessage}`);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // 获取学生余额信息 - 使用已保存的 studentId
  const getStudentBalance = async (linkedStudentId, isRefresh = false) => {
    try {
      // 使用传入的学生ID，如果没有则使用状态中的
      const targetStudentId = linkedStudentId || studentId;
      
      if (!targetStudentId) {
        console.warn('无法获取余额信息：学生ID缺失');
        return { totalRemainingHours: 0, totalRemainingAmount: 0 };
      }
      
      console.log('获取学生余额信息，学生ID:', targetStudentId, '用户角色:', role);
      
      // 获取学生购买记录
      let purchases;
      try {
        // 学生角色必须使用 /me/purchase-records 端点（没有通用购买记录权限）
        const isStudentRole = role === 'student';
        console.log('isStudentRole:', isStudentRole, 'role:', role);
        
        if (isStudentRole) {
          console.log('学生角色，通过 /me/purchase-records 获取购买记录');
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
          let url = `${apiBaseUrl}/api/auth/me/purchase-records`;
          if (isRefresh) {
            url += '?is_refresh=true';
          }
          console.log('请求URL:', url);
          
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Content-Type': 'application/json',
            },
          });
          
          console.log('响应状态:', response.status, response.statusText);
          
          if (response.ok) {
            purchases = await response.json();
            console.log('成功获取购买记录:', purchases);
          } else {
            // 学生角色没有通用购买记录权限，不回退到 purchaseService
            const errorText = await response.text();
            console.warn(`/me/purchase-records 请求失败: ${response.status}，错误: ${errorText}`);
            return { totalRemainingHours: 0, totalRemainingAmount: 0 };
          }
        } else {
          // 非学生角色使用通用端点
          console.log('非学生角色，使用通用端点获取购买记录');
          purchases = await purchaseService.getList({
            student_id: targetStudentId,
            page: 1,
            per_page: 100,
          });
        }
      } catch (err) {
        console.error('获取购买记录失败:', err);
        return { totalRemainingHours: 0, totalRemainingAmount: 0 };
      }
      
      let totalRemainingHours = 0;
      let totalRemainingAmount = 0;
      
      if (purchases.items) {
        purchases.items.forEach(purchase => {
          totalRemainingHours += purchase.remaining_hours || 0;
          totalRemainingAmount += purchase.remaining_amount || 0;
        });
      }
      
      console.log('余额统计结果:', { totalRemainingHours, totalRemainingAmount });
      return { totalRemainingHours, totalRemainingAmount };
    } catch (err) {
      console.error('获取余额信息失败:', err);
      return { totalRemainingHours: 0, totalRemainingAmount: 0 };
    }
  };

  // 初始加载
  useEffect(() => {
    const isTokenAccess = localStorage.getItem('access_token') !== null;
    
    if (isTokenAccess && role === 'student') {
      // 令牌访问模式下直接加载
      loadStudentInfo();
    } else if (user) {
      if (role === 'student') {
        // 学生角色：尝试加载学生信息
        if (user.id) {
          loadStudentInfo();
        } else {
          setError('无法获取学生ID');
          setLoading(false);
        }
      } else if (role && role !== 'student') {
        // 非学生角色访问学生页面，显示错误
        setError('非学生角色用户无法访问此页面');
        setLoading(false);
      } else if (role === undefined || role === null) {
        // 角色未定义时，默认按学生角色处理（兼容演示模式）
        if (user.id) {
          loadStudentInfo();
        } else {
          setError('用户信息不完整');
          setLoading(false);
        }
      }
    } else {
      setLoading(false);
    }
  }, [user, role]);

  // 处理登出
  const handleLogout = () => {
    // 1. 立即清除本地状态并关闭弹出层（极致响应感）
    logout();
    if (onClose) onClose();

    // 2. 异步发送登出请求，不阻塞 UI
    authService.logout().catch(e => console.error('Silent logout error:', e));

    // 3. 极短时间后执行重定向，确保状态同步
    setTimeout(() => {
      window.location.href = '/';
    }, 50);
  };

  // 处理密码修改
  const handlePasswordChange = async () => {
    // 验证输入
    setPasswordError('');
    
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('请填写所有字段');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('新密码至少需要8个字符');
      return;
    }
    
    // 检查是否包含字母
    const hasLetter = /[a-zA-Z]/.test(passwordForm.newPassword);
    // 检查是否包含数字
    const hasNumber = /[0-9]/.test(passwordForm.newPassword);
    
    if (!hasLetter || !hasNumber) {
      setPasswordError('新密码必须同时包含字母和数字');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('两次输入的新密码不一致');
      return;
    }
    
    if (passwordForm.oldPassword === passwordForm.newPassword) {
      setPasswordError('新密码不能与旧密码相同');
      return;
    }
    
    try {
      setPasswordLoading(true);
      await authService.changePassword(passwordForm.oldPassword, passwordForm.newPassword);
      
      toast.success('密码修改成功，请重新登录');
      
      // 重置表单
      setPasswordForm({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordModal(false);
      
      // 延迟登出，让用户看到成功提示
      setTimeout(() => {
        handleLogout();
      }, 1500);
    } catch (error) {
      console.error('密码修改失败:', error);
      setPasswordError(error.message || '密码修改失败，请检查旧密码是否正确');
    } finally {
      setPasswordLoading(false);
    }
  };

  // 打开密码修改模态框
  const openPasswordModal = () => {
    setPasswordForm({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setShowPasswordModal(true);
  };

  // 计算积分
  const calculatePoints = () => {
    return studentInfo?.points || 0;
  };

  // 设置微信分享
  useEffect(() => {
    if (studentInfo && studentInfo.name) {
      setWeChatShare({
        uiTitle: `${studentInfo.name} 夏天艺术工作室`,
        title: SHARE_TEMPLATE(studentInfo.name),
        desc: `这里记录了 ${studentInfo.name} 在夏天艺术工作室的艺术成长点滴。`,
      });
    }
  }, [studentInfo]);

  // 获取课程结余
  const [balanceInfo, setBalanceInfo] = useState({ totalRemainingHours: 0, totalRemainingAmount: 0 });
  const [rewardBalance, setRewardBalance] = useState({ available_reward: 0, accumulating_reward: 0, in_transit_reward: 0 });
  const [rewardRecords, setRewardRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  
  useEffect(() => {
    const loadBalanceInfo = async () => {
      try {
        // 使用已保存的学生ID获取余额信息
        const balance = await getStudentBalance(studentId, isRefreshing);
        setBalanceInfo(balance);

          try {
          const rBalance = await rewardService.getBalance(studentId);
          setRewardBalance(rBalance);
          
          // 获取赠送记录 (包含推荐和达标赠送)
          setLoadingRecords(true);
          const records = await rewardService.getReferrals(studentId);
          setRewardRecords(records);
        } catch (e) {
          console.warn('获取赠送详情失败:', e);
        } finally {
          setLoadingRecords(false);
        }
      } catch (err) {
        console.error('加载余额信息失败:', err);
        setBalanceInfo({ totalRemainingHours: 0, totalRemainingAmount: 0 });
      }
    };
    
    // 当学生信息和学生ID都已加载时，加载余额信息
    if (studentInfo && studentId) {
      loadBalanceInfo();
    }
  }, [studentInfo, studentId]);

  return (
    <div className="flex flex-col bg-[#F2F2F7]" style={{ minHeight: 0 }}>

      {/* ── iOS Grouped Content ── */}
      <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>

        {/* 错误提示 */}
        {error && (
          <div className="mx-4 mt-4 bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
            <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">加载失败：{error}</p>
              <button onClick={() => loadStudentInfo(true)} className="mt-1 text-xs text-red-600 underline">重试</button>
            </div>
          </div>
        )}

        {/* 加载中 */}
        {loading && !studentInfo && (
          <div className="flex flex-col items-center justify-center py-20">
            <RefreshCw size={28} className="text-[#3182CE] animate-spin mb-3" />
            <p className="text-sm text-gray-400">加载中…</p>
          </div>
        )}

        {studentInfo && (
          <>
            {/* ── Section 1：课时看板 ── */}
            <p className="px-5 pt-6 pb-1.5 text-[13px] font-medium text-gray-500 tracking-wide uppercase">课时余额</p>
            <div className="mx-4 rounded-2xl overflow-hidden bg-white shadow-sm">
              {/* 正课行 */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#3182CE,#63B3ED)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                  </div>
                  <span className="text-[15px] font-medium text-gray-800">正课</span>
                </div>
                <span className="text-[22px] font-bold text-gray-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatHours(balanceInfo.totalRemainingHours)}
                </span>
              </div>
              {/* 赠课·在途行 */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#F6AD55,#FBBF24)' }}>
                    <Trophy size={15} color="white" strokeWidth={2.2} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[15px] font-medium text-gray-800">赠课</span>
                    <span className="text-[11px] text-gray-400">在途</span>
                  </div>
                </div>
                <span className="text-[22px] font-bold text-amber-500" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatHours(rewardBalance.in_transit_reward)}
                </span>
              </div>
              {/* 赠课·可用行 */}
              <div className="flex items-center justify-between px-4 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#FC8181,#F687B3)' }}>
                    <Trophy size={15} color="white" strokeWidth={2.2} />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[15px] font-medium text-gray-800">赠课</span>
                    <span className="text-[11px] text-gray-400">可用</span>
                  </div>
                </div>
                <span className="text-[22px] font-bold text-rose-500" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatHours(rewardBalance.available_reward)}
                </span>
              </div>
            </div>

            {/* ── Section 2：赠送明细 ── */}
            <p className="px-5 pt-6 pb-1.5 text-[13px] font-medium text-gray-500 tracking-wide uppercase">
              赠送明细
              {rewardRecords.length > 0 && (
                <span className="ml-2 text-[12px] text-gray-400 normal-case font-normal">{rewardRecords.length} 条</span>
              )}
            </p>
            <div className="mx-4 rounded-2xl overflow-hidden bg-white shadow-sm">
              {loadingRecords ? (
                <div className="py-10 flex justify-center">
                  <RefreshCw size={22} className="text-blue-400 animate-spin" />
                </div>
              ) : rewardRecords.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {rewardRecords.map((ref, idx) => (
                    <div key={ref.id} className="flex items-center justify-between px-4 py-3.5 active:bg-gray-50 transition-colors">
                      <div className="flex flex-col min-w-0 mr-3">
                        <span className="text-[15px] font-semibold text-gray-800 leading-snug truncate">{ref.referee_name}</span>
                        <span className="text-[11px] text-gray-400 mt-0.5">
                          {ref.created_at} · {ref.type === 'REFERRAL' ? '推荐赠送' : '达标赠送'}
                        </span>
                      </div>
                      <div className="flex flex-col items-end flex-shrink-0 gap-1">
                        <span className={`text-[15px] font-bold ${ref.status === 'CANCELLED' ? 'text-gray-300 line-through' : 'text-blue-600'}`}>
                          +{formatHours(ref.amount)} 节
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          ref.status === 'COMPLETED' ? 'bg-green-100 text-green-700'
                          : ref.status === 'IN_TRANSIT' ? 'bg-orange-100 text-orange-600'
                          : 'bg-gray-100 text-gray-500'
                        }`}>
                          {ref.status === 'COMPLETED' ? '已发放' : ref.status === 'IN_TRANSIT' ? '在途' : ref.status === 'CANCELLED' ? '已作废' : ref.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 flex flex-col items-center gap-2">
                  <Trophy size={28} className="text-gray-200" />
                  <p className="text-[13px] text-gray-400">暂无赠送记录，继续加油哦</p>
                </div>
              )}
            </div>
            
            {/* ── Section 3：系统设置 ── */}
            <p className="px-5 pt-6 pb-1.5 text-[13px] font-medium text-gray-500 tracking-wide uppercase">系统设置</p>
            <div className="mx-4 rounded-2xl overflow-hidden bg-white shadow-sm divide-y divide-gray-100">
              <button 
                onClick={openPasswordModal}
                className="w-full flex items-center justify-between px-4 py-4 active:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <Key size={16} className="text-gray-600" />
                  </div>
                  <span className="text-[15px] font-medium text-gray-800">修改密码</span>
                </div>
                <ChevronRight size={18} className="text-gray-300" />
              </button>

              <button 
                onClick={handleLogout}
                className="w-full flex items-center justify-between px-4 py-4 active:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                    <LogOut size={16} className="text-red-500" />
                  </div>
                  <span className="text-[15px] font-medium text-red-500">退出登录</span>
                </div>
                <ChevronRight size={18} className="text-gray-300" />
              </button>
            </div>



            <div className="h-10" />
          </>
        )}

        {/* 空状态 */}
        {!loading && !error && !studentInfo && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CBD5E0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </div>
            <p className="text-[14px] text-gray-400">暂无学生信息</p>
          </div>
        )}
      </div>

      {/* ── 修改密码 Modal（iOS Sheet 风格）── */}
      {showPasswordModal && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          onClick={() => setShowPasswordModal(false)}
        >
          <div
            className="w-full bg-white rounded-t-3xl px-5 pt-5 pb-8 shadow-2xl"
            style={{ animation: 'sheetSlideUp 0.28s cubic-bezier(0.32,1,0.56,1) both' }}
            onClick={e => e.stopPropagation()}
          >
            {/* 拖拽条 */}
            <div className="flex justify-center mb-4">
              <div className="w-9 h-1 rounded-full bg-gray-300" />
            </div>

            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[17px] font-semibold text-gray-900">修改密码</h3>
              <button onClick={() => setShowPasswordModal(false)} disabled={passwordLoading}
                className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                <X size={15} className="text-gray-500" />
              </button>
            </div>

            {passwordError && (
              <div className="mb-4 p-3 bg-red-50 rounded-xl flex items-start gap-2">
                <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-[13px] text-red-600">{passwordError}</p>
              </div>
            )}

            <div className="space-y-3">
              {[
                { label: '旧密码', key: 'oldPassword', placeholder: '请输入当前密码' },
                { label: '新密码', key: 'newPassword', placeholder: '至少8个字符，含字母和数字' },
                { label: '确认新密码', key: 'confirmPassword', placeholder: '再次输入新密码' },
              ].map(({ label, key, placeholder }) => (
                <div key={key}>
                  <label className="block text-[13px] text-gray-500 mb-1 pl-1">{label}</label>
                  <input
                    type="password"
                    value={passwordForm[key]}
                    onChange={e => setPasswordForm({ ...passwordForm, [key]: e.target.value })}
                    placeholder={placeholder}
                    disabled={passwordLoading}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-[15px] placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition"
                  />
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowPasswordModal(false)} disabled={passwordLoading}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-[15px] text-gray-600 font-medium active:bg-gray-50 transition">
                取消
              </button>
              <button onClick={handlePasswordChange} disabled={passwordLoading}
                className="flex-1 py-3 rounded-xl bg-blue-500 text-white text-[15px] font-semibold active:bg-blue-600 transition disabled:opacity-50 flex items-center justify-center gap-2">
                {passwordLoading ? <><RefreshCw size={16} className="animate-spin" />处理中…</> : '确认修改'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StudentProfileView;